import { Component } from 'react';
import { connect } from 'react-redux';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import * as htmlToImage from 'html-to-image';
import { OZ } from 'HubStyleTokens/colors';
import UIProgress from 'UIComponents/progress/UIProgress';
import UIAlert from 'UIComponents/alert/UIAlert';
import TrackableButton from 'automation-ui-usage-tracking/components/TrackableButton';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import * as platformFlowsApplicationActions from 'automation-ui-canvas/actions/PlatformFlowsApplicationActions';
import * as CanvasIdentifiers from 'automation-ui-flow-core/canvas/CanvasIdentifiers';
import UIAbstractProgress from 'UIComponents/progress/UIAbstractProgress';
import UIIcon from 'UIComponents/icon/UIIcon';
import { requestIdleCallback } from 'automation-ui-canvas/utils/requestIdleCallback';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIButton from 'UIComponents/button/UIButton';
import { H2 } from 'UIComponents/elements/headings';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import { getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_DIMENSION_PIXELS = 14000;
class ExportWorkflowModal extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      isExporting: false,
      failedToExport: false,
      progressLabel: null,
      finishedExportingSuccessfully: false
    };
    this.idleCallbackInterval = undefined;
    this.handleFinishedExporting = () => {
      const {
        setCanvasVirtualRenderingToDisabled,
        onConfirm
      } = this.props;
      this.setState({
        finishedExportingSuccessfully: true
      }, () => {
        setCanvasVirtualRenderingToDisabled(false);
        onConfirm();
      });
    };
    this.handleClickedExport = () => {
      const {
        setCanvasVirtualRenderingToDisabled
      } = this.props;
      return this.setState({
        isExporting: true,
        progressLabel: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "edit.exportModal.drawingWorkflow"
        })
      }, () => {
        const node = document.getElementById(CanvasIdentifiers.STARTING_NODE_ID);
        if (!node) {
          this.setState({
            isExporting: false,
            failedToExport: true
          });
          return;
        }
        setCanvasVirtualRenderingToDisabled(true);
        this.idleCallbackInterval = setInterval(() => {
          requestIdleCallback(idleDeadline => {
            if (!idleDeadline.didTimeout) {
              clearInterval(this.idleCallbackInterval);
              this.convertDomToImage(node);
            }
          }, {
            timeout: 100
          });
        }, 1000);
      });
    };
    this.convertDomToImage = node => {
      const {
        flow,
        zoomLevel
      } = this.props;
      const width = node.offsetWidth;
      const height = node.offsetHeight / zoomLevel;
      const scaleWidth = MAX_DIMENSION_PIXELS / width;
      const scaleHeight = MAX_DIMENSION_PIXELS / height;
      const minScale = Math.min(scaleWidth, scaleHeight, 1);
      this.setState({
        progressLabel: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "edit.exportModal.exportingImage"
        })
      }, () => {
        htmlToImage.toBlob(node, {
          width: width * minScale,
          height: height * minScale,
          /* Text isn't readable when scaled down this far, so skip embedding
             the font in the rasterization process */
          skipFonts: minScale < 0.3,
          style: {
            transform: `scale(${minScale})`,
            transformOrigin: `0px top`
          }
        }).then(blob => {
          if (blob) {
            const dataUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${flow.getName()}.png`;
            link.href = dataUrl;
            link.click();
            this.handleFinishedExporting();
          } else {
            throw new Error('Error creating workflows exported image: Blob is null');
          }
        }).catch(error => {
          this.setState({
            failedToExport: true,
            isExporting: false
          });
          console.error('Error creating workflows exported image!', error);
        });
      });
    };
  }
  componentWillUnmount() {
    clearInterval(this.idleCallbackInterval);
  }
  renderProgressBar() {
    const {
      isExporting,
      failedToExport,
      progressLabel,
      finishedExportingSuccessfully
    } = this.state;
    if (!isExporting || failedToExport) {
      return null;
    }
    return /*#__PURE__*/_jsx(UIAbstractProgress, {
      incrementFactor: 0.05,
      maximum: 90,
      render: ({
        value
      }) => /*#__PURE__*/_jsx(UIProgress, {
        use: "success",
        value: finishedExportingSuccessfully ? 100 : value,
        valueLabel: /*#__PURE__*/_jsx(FormattedPercentage, {
          value: value
        }),
        descriptiveLabel: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx("span", {
            className: "m-right-2",
            children: progressLabel
          }), finishedExportingSuccessfully ? /*#__PURE__*/_jsx(UIIcon, {
            name: "success",
            color: OZ
          }) : null]
        })
      })
    });
  }
  renderError() {
    const {
      failedToExport,
      isExporting
    } = this.state;
    if (failedToExport && !isExporting) {
      return /*#__PURE__*/_jsx(UIAlert, {
        titleText: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "edit.exportModal.errorDownloadingTitle"
        }),
        type: "danger"
      });
    }
    return null;
  }
  render() {
    const {
      onReject
    } = this.props;
    const {
      isExporting
    } = this.state;
    return /*#__PURE__*/_jsxs(UIModal, {
      size: "auto",
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "edit.exportModal.header"
          })
        }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: onReject
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [/*#__PURE__*/_jsx("div", {
          className: "m-bottom-4",
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "edit.exportModal.description"
          })
        }), this.renderProgressBar(), this.renderError()]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(TrackableButton, {
          onClick: this.handleClickedExport,
          use: "primary",
          type: "submit",
          disabled: isExporting,
          trackableKey: Trackables.EXPORT_WORKFLOW_TO_IMAGE,
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "edit.exportModal.submit"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: onReject,
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "edit.cancel"
          })
        })]
      })]
    });
  }
}
const mapDispatchToProps = {
  setCanvasVirtualRenderingToDisabled: platformFlowsApplicationActions.setCanvasVirtualRenderingToDisabled
};
const mapStateToProps = (state, {
  flow
}) => {
  const flowId = flow.getFlowId();
  return {
    zoomLevel: getZoomLevel(flowId)(state)
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(ExportWorkflowModal);